// Re-export all utilities from the new modular structure
import api from "api/api";
import { Pipeline } from ".";
import { Toast } from "components/toast";
import { makeEvent } from "services/events";
import { Dispatch, SetStateAction } from "react";
import { MailingTableRow } from "..";
import { createLinkAndDownload } from "utils/create-link-and-download";
import { openModalFields } from "components/modal-fields";
import { fieldValidation } from "utils/field-validation";
import { ValidationErrorsProps } from "./types";

export * from "./utils/pipeline-helpers";
export * from "./utils/config-parser";
export type { ValidationErrorsProps } from "./types";
export { validationErrorsMap } from "./types";

// Keep legacy functions that are used elsewhere

export const advancePipeline = async ({
  setActiveStep,
  setIsLoading,
  row,
}: {
  setActiveStep: Dispatch<SetStateAction<number>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  row: MailingTableRow;
}) => {
  setIsLoading(true);
  try {
    const response = await api.post(`/pipeline/advance/${row.id}`);
    makeEvent(`mailing-update-${row.id}`);
  } catch (e) {
    Toast({
      title: "Erro ao avançar pipeline",
      status: "error",
    });
  } finally {
    setIsLoading(false);
    makeEvent("update-mailings-table", true);
  }
};

export const replayPipeline = async ({
  higienizacaoIds,
  row,
  setIsLoading,
}: {
  higienizacaoIds: Array<string> | null;
  row: MailingTableRow;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}) => {
  if (higienizacaoIds) {
    setIsLoading(true);
    try {
      await api.post(`/pipeline/replay/${row.id}`, higienizacaoIds);
    } catch (e) {
      Toast({
        title: "Erro ao executar novamente o pipeline",
        status: "error",
      });
    } finally {
      setIsLoading(false);
      makeEvent("update-mailings-table", true);
    }
  }
};

export const verifyStageStatus = ({
  stage,
  status,
  data,
  modalData,
}: {
  stage: Pipeline["pipelineStage"];
  status: Pipeline["pipelineStatus"];
  data?: Pipeline[];
  modalData: Pipeline[];
}) => {
  if (data)
    return !!data
      .filter((crr) => crr.pipelineStage === stage)
      .filter((crr) => crr.pipelineStatus === status).length;
  return !!modalData
    .filter((crr) => crr.pipelineStage === stage)
    .filter((crr) => crr.pipelineStatus === status).length;
};

export async function getValidationErrors(
  digitacaoId: number,
  pipelineItemId: number,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setValidationErrors: React.Dispatch<
    React.SetStateAction<{
      ENDERECO?: number;
      DADOS_BANCARIOS?: number;
      GENERIC?: number;
    }>
  >
) {
  setIsLoading?.(true);
  try {
    const { data } = await api.get<ValidationErrorsProps>(
      `/pipeline/pipeline-item/${pipelineItemId}/digitacao/${digitacaoId}/validation-errors`
    );
    setValidationErrors(data);
  } catch (e) {
  } finally {
    setIsLoading?.(false);
  }
}

export async function downloadValidationErrors(
  digitacaoId: number,
  pipelineItemId: number,
  errorType: keyof ValidationErrorsProps
) {
  try {
    const { data } = await api.get<string>(
      `/pipeline/pipeline-item/${pipelineItemId}/digitacao/${digitacaoId}/download/errors`,
      { params: { errorType } }
    );
    const file = new Blob([data]);
    createLinkAndDownload(file, `mailings-com-erros-${errorType}.csv`);
  } catch {
  } finally {
  }
}

export async function uploadValidationErrors(
  file: File,
  digitacaoId: number,
  pipelineItemId: number,
  errorType: keyof ValidationErrorsProps
) {
  const formData = new FormData();
  formData.append("files", file);
  await api.put(
    `/pipeline/pipeline-item/${pipelineItemId}/digitacao/${digitacaoId}/fix/upload`,
    formData,
    { params: { errorType } }
  );
}

async function changeUsuarioBanco({
  pipelineStage,
  params,
}: {
  pipelineStage: Pipeline;
  params: { [key: string]: string };
}) {
  try {
    await api.put(
      `/pipeline/pipeline-item/${pipelineStage.pipelineItemId}/fix`,
      params
    );
    Toast({ title: "Usuário banco alterado com sucesso", status: "success" });
  } catch (e: any) {
    const errorMessage = e?.response?.data?.message;
    Toast({
      title: "Erro ao alterar usuário banco",
      description: errorMessage,
    });
  }
}

export function openModalChangeUsuario({
  pipelineStage,
  row,
  usuarioDigitacao,
  usuarioDigitacaoSenha,
}: {
  pipelineStage: Pipeline;
  row: MailingTableRow;
  usuarioDigitacaoSenha: string;
  usuarioDigitacao: string;
}) {
  const banco = pipelineStage.banco;
  openModalFields({
    title: "Alterar usuário banco",
    paramsSettings: {
      params: [
        {
          name: "Usuário",
          field: "username",
          initialValue: usuarioDigitacao,
        },
        {
          name: "Senha",
          field: "password",
          initialValue: usuarioDigitacaoSenha,
        },
      ],
    },
    schema: {
      username: (value) => {
        const { isValid, errorMessage } = fieldValidation({ value })
          .required({
            message: "Preencha o campo",
          })
          .custom({
            validate: (data) => (banco === "C6" ? data.includes("_") : true),
            message: `O campo Usuário precisa ter um pelo menos um caractere underline "_"`,
          });
        return {
          valid: isValid,
          message: errorMessage,
        };
      },
      password: (value) => {
        const { isValid, errorMessage } = fieldValidation({ value }).required({
          message: "Preencha o campo",
        });
        return { valid: isValid, message: errorMessage };
      },
    },
    onSubmit: async (params) => {
      await changeUsuarioBanco({ pipelineStage, params });
      makeEvent(`mailing-update-${row.id}`);
    },
  });
}
